/**
 * Dokument-Metadaten eines Leadsheets: Titel und Tempo.
 *
 * Diese liegen bewusst außerhalb von {@link DrumPattern}: Das Pattern
 * bleibt rein musikalisch (Raster/Schläge), Titel und Tempo sind
 * Angaben des Proben-Leadsheets. Rein fachlich, ohne DOM-Bezug.
 */

export interface LeadsheetMeta {
  readonly title: string;
  /** Tempo in Schlägen pro Minute (BPM). */
  readonly tempo: number;
}

export const DEFAULT_TEMPO = 120;
export const MIN_TEMPO = 20;
export const MAX_TEMPO = 400;
export const MAX_TITLE_LENGTH = 80;

export const DEFAULT_META: LeadsheetMeta = {
  title: '',
  tempo: DEFAULT_TEMPO,
};

export const META_FORMAT_VERSION = 1;

/** Fehler bei ungültigen bzw. beschädigten Metadaten. */
export class LeadsheetMetaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LeadsheetMetaError';
  }
}

/** Bereinigt einen Titel: trimmen und auf die Maximallänge kürzen. */
export function normalizeTitle(raw: unknown): string {
  if (typeof raw !== 'string') {
    return '';
  }
  return raw.trim().slice(0, MAX_TITLE_LENGTH);
}

/** Prüft, ob ein Wert ein gültiges Tempo (ganzzahlig, im Bereich) ist. */
export function isValidTempo(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_TEMPO &&
    value <= MAX_TEMPO
  );
}

/**
 * Begrenzt ein Tempo auf den gültigen Bereich. Nicht-endliche Werte
 * werden auf das Standardtempo abgebildet.
 */
export function clampTempo(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_TEMPO;
  }
  const rounded = Math.round(value);
  return Math.min(MAX_TEMPO, Math.max(MIN_TEMPO, rounded));
}

/** Erzeugt gültige Metadaten aus beliebigen Eingaben. */
export function makeMeta(title: unknown, tempo: unknown): LeadsheetMeta {
  return { title: normalizeTitle(title), tempo: clampTempo(tempo) };
}

export interface SerializedMeta {
  readonly version: number;
  readonly title: string;
  readonly tempo: number;
}

/** Überführt Metadaten in eine JSON-taugliche Struktur. */
export function serializeMeta(meta: LeadsheetMeta): SerializedMeta {
  return {
    version: META_FORMAT_VERSION,
    title: meta.title,
    tempo: meta.tempo,
  };
}

/**
 * Stellt Metadaten aus einer serialisierten Struktur wieder her.
 * Grob defekte Daten (falscher Typ/Version) werden abgelehnt; benigne
 * Abweichungen (Whitespace, Tempo außerhalb des Bereichs) werden
 * normalisiert.
 */
export function deserializeMeta(data: unknown): LeadsheetMeta {
  if (typeof data !== 'object' || data === null) {
    throw new LeadsheetMetaError('Metadaten müssen ein Objekt sein.');
  }
  const record = data as Record<string, unknown>;
  if (record.version !== META_FORMAT_VERSION) {
    throw new LeadsheetMetaError(
      `Nicht unterstützte Metadaten-Version: ${String(record.version)}`,
    );
  }
  if (typeof record.title !== 'string') {
    throw new LeadsheetMetaError('Feld "title" muss ein String sein.');
  }
  if (typeof record.tempo !== 'number' || !Number.isFinite(record.tempo)) {
    throw new LeadsheetMetaError('Feld "tempo" muss eine endliche Zahl sein.');
  }
  return makeMeta(record.title, record.tempo);
}
